import { useState } from 'react';
import { LIMITS, type MeResponse } from '@stonelink/shared';
import { useSearchParams } from 'react-router-dom';
import { api, ApiError } from '../api';
import { Button, ErrorNote, Eyebrow, Input, Label } from '../ui';

/**
 * The other end of an invitation link. Public, because the person accepting has
 * no account yet — the token in the URL is what vouches for them.
 */
export function AcceptInvitePage({ onSignedIn }: { onSignedIn: (session: MeResponse) => void }) {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      onSignedIn(await api.acceptInvite({ token, password }));
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not accept that invitation.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-full items-center justify-center p-6">
      <form onSubmit={(event) => void submit(event)} className="glass w-full max-w-sm p-8">
        <div className="mb-7">
          <Eyebrow>Stonelink Assist</Eyebrow>
          <h1 className="font-display text-4xl leading-none">
            You’ve been <em className="text-brand italic">invited.</em>
          </h1>
          <p className="mt-3 text-sm text-soft">
            Pick a password and you are in. Your email came with the invitation.
          </p>
        </div>

        {!token ? (
          <ErrorNote>
            This link is missing its invitation code. Ask whoever invited you for a new one.
          </ErrorNote>
        ) : (
          <div className="space-y-4">
            <div>
              <Label htmlFor="password">Choose a password</Label>
              <Input
                id="password"
                type="password"
                autoComplete="new-password"
                required
                minLength={LIMITS.password.min}
                value={password}
                onChange={(event) => setPassword(event.target.value)}
              />
              <p className="mt-2 text-xs text-muted">At least {LIMITS.password.min} characters.</p>
            </div>

            {error && <ErrorNote>{error}</ErrorNote>}

            <Button type="submit" className="w-full" disabled={busy}>
              {busy ? 'Joining…' : 'Join the workspace'}
            </Button>
          </div>
        )}
      </form>
    </div>
  );
}
